<template>
  <b-sidebar
    id="help-modal"
    v-model="visible"
    bg-variant="white"
    right
    backdrop
    shadow
    lazy
    :title="$t('help.help')"
    @shown="fetchSections"
  >
    <template #header="{hide}">
      <help-modal-header
        :section.sync="selectedSection"
        @close="hide"
      />
    </template>

    <div class="px-1 d-flex justify-content-between flex-column h-100">
      <div class="flex-grow-1">
        <help-search-input v-model.trim="search" />

        <error-alert :error="error" />

        <div
          v-if="loading"
          class="text-center my-4 description-text"
        >
          <b-spinner />
        </div>

        <help-questions-list
          v-if="showQuestionsList"
          :section.sync="selectedSection"
          :search.sync="search"
          :questions="questions"
        />

        <help-sections-list
          v-else
          @select="selectedSection = $event; search = ''"
        />
      </div>

      <div class="border-top py-1">
        <b-button
          variant="secondary"
          size="lg"
          block
          @click="openFreshchat"
        >
          {{ $t('help.live-support') }}
        </b-button>
      </div>
    </div>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BSpinner, BButton,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'
import LocalizationMixin from '@/mixins/localization-mixin'
import HelpModalHeader from '@/@okmenu/components/help/HelpModalHeader'
import HelpSectionsList from '@/@okmenu/components/help/HelpSectionsList'
import HelpQuestionsList from '@/@okmenu/components/help/HelpQuestionsList'
import HelpSearchInput from '@/@okmenu/components/help/HelpSearchInput'
import FreshchatService from '@/services/FreshchatService'

export default {
  name: 'HelpModal',
  components: {
    HelpSearchInput,
    HelpQuestionsList,
    HelpSectionsList,
    HelpModalHeader,
    ErrorAlert,
    BSpinner,
    BSidebar,
    BButton,
  },
  mixins: [LocalizationMixin],
  data() {
    return {
      search: '',
      loading: false,
      error: null,
      selectedSection: null,
      visible: false,
    }
  },
  computed: {
    ...mapGetters({
      helpSections: 'help/questionSections',
    }),
    showQuestionsList() {
      return (this.search && this.questions.length) || this.selectedSection
    },
    allQuestions() {
      const questions = this.helpSections?.reduce((acc, section) => acc.concat(section.questions), []) ?? []
      return questions.map(question => ({ id: question.id, question: this.getTranslatedField(question, 'question'), answer: this.getTranslatedField(question, 'answer') }))
    },
    sectionQuestions() {
      const questions = this.selectedSection?.questions ?? []
      return questions.map(question => ({ id: question.id, question: this.getTranslatedField(question, 'question'), answer: this.getTranslatedField(question, 'answer') }))
    },
    questions() {
      const questions = this.selectedSection ? this.sectionQuestions : this.allQuestions

      if (!this.search) {
        return questions
      }

      return questions
        .filter(question => (question.question + question.answer).toLowerCase().includes(this.search.toLowerCase()))
        .map(({ question, answer, id }) => ({ id, question: this.highlightText(question, this.search), answer: this.highlightText(answer, this.search) }))
    },
  },
  methods: {
    fetchSections() {
      this.loading = true
      this.error = null

      this.$store.dispatch('help/fetchHelpSections')
        .catch(error => { this.error = error })
        .finally(() => { this.loading = false })
    },
    highlightText(text, query) {
      return text.replace(new RegExp(query, 'ig'), v => `<span class='bg-secondary text-white'>${v}</span>`)
    },
    openFreshchat() {
      this.visible = false
      FreshchatService.openFreshchat()
    },
  },
}
</script>

<style lang="scss">
#help-modal {
  transition: 0.4s all;

  .questions {
    max-height: calc(100vh - 275px)!important;
    overflow: auto;

    .answer {
      margin-top: 10px;

      &, & * {
        font-size: 14px!important;
        p {
          margin-bottom: 7px!important;
        }
      }
    }
  }
}
</style>
